import type {
  AgentPresentationStep,
  ResolvedPresentationRect,
} from "@/lib/agent-presentation-types";
import { sketchShapePoints } from "@/lib/sketch-stroke/sketch-mark";
import { strokeLength } from "@/lib/sketch-stroke/stroke-points";

export type DrawPoint = { x: number; y: number };

/** The pen route of a draw step, normalized to its rect. Open gestures keep
 * the agent's raw points (agent ink smooths them); closed shapes are the
 * seeded hand-drawn sketch, so every display draws the same wobble. */
export function normalizedDrawPath(
  step: Extract<AgentPresentationStep, { kind: "draw" }>,
  rect: Pick<ResolvedPresentationRect, "width" | "height">,
  seed: string,
): DrawPoint[] | null {
  if (
    step.shape === "path" ||
    step.shape === "polyline" ||
    step.shape === "arrow"
  ) {
    return step.path && step.path.length >= 2 ? step.path : null;
  }
  const width = Math.max(1, rect.width);
  const height = Math.max(1, rect.height);
  return sketchShapePoints(step.shape, width, height, seed).map((point) => ({
    x: point.x / width,
    y: point.y / height,
  }));
}

export function globalDrawPath(
  normalized: DrawPoint[],
  rect: ResolvedPresentationRect,
): DrawPoint[] {
  return normalized.map((point) => ({
    x: rect.x + point.x * rect.width,
    y: rect.y + point.y * rect.height,
  }));
}

export function drawDurationMs(
  points: DrawPoint[],
  pace: "quick" | "normal" | "slow" = "normal",
): number {
  const paceFactor = pace === "quick" ? 0.72 : pace === "slow" ? 1.35 : 1;
  // Keep the gesture traceable while leaving reading time to narration/holds.
  return Math.round(
    Math.max(250, Math.min(900, strokeLength(points) * 0.6)) * paceFactor,
  );
}
